import { useState, useEffect } from "react";

const BASE_URL = process.env.REACT_APP_API_BASE_URL

const QuizViewer = ({ lesson }) => {
  const [quizState, setQuizState] = useState("overview"); // overview, taking, completed
  const [quizData, setQuizData] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  //console.log("lesson : ", lesson.id)

  // Fetch quiz data
  useEffect(() => {
    const fetchQuizData = async () => {
      if (!lesson) return;

      try {
        const token = localStorage.getItem("access");
        const response = await fetch(`${BASE_URL}/courses/lessons/${lesson.id}/quiz/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch quiz data");
        }

        const data = await response.json();
        setQuizData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchQuizData();
  }, [lesson]);

  // Fetch quiz attempts
  useEffect(() => {
    const fetchQuizAttempts = async () => {
      if (!quizData) return;

      try {
        const token = localStorage.getItem("access");
        const response = await fetch(`${BASE_URL}/quiz/quiz-attempts/?quiz=${quizData.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json()
          setQuizAttempts(data.results || [])

        }
      } catch (err) {
        //console.error("Failed to fetch quiz attempts:", err);
      }
    };

    fetchQuizAttempts();
  }, [quizData]);

  const handleAnswerChange = (questionId, optionId) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: optionId,
    }));
  };

  const handleStartQuiz = () => {
    setQuizState("taking");
    setSelectedAnswers({});
    setCurrentQuestionIndex(0);
  };

  const handleSubmitQuiz = async () => {
    setSubmitting(true);
    try {
      // Calculate score based on correct answers (for local validation)
      let correctAnswers = 0;
      const totalQuestions = quizData.questions.length;

      quizData.questions.forEach((question) => {
        const selectedOption = selectedAnswers[question.id];
        const correctOption = question.options.find((opt) => opt.is_correct);
        if (selectedOption === correctOption?.id) {
          correctAnswers++;
        }
      });

      const score = Math.round((correctAnswers / totalQuestions) * 100);

      // Submit quiz attempt to backend
      const token = localStorage.getItem("access");
      const response = await fetch(`${BASE_URL}/quiz/submit/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          quiz: quizData.id,
          answers: selectedAnswers, // Send as { "1": 2, "2": 5, ... }
        }),
      });

      if (response.ok) {
        const submissionData = await response.json()

        // Update UI with actual result
        setQuizAttempts(prev => [...prev, {
          id: submissionData.attempt_id,
          score: submissionData.score,
          completed_at: new Date().toISOString(), // fallback
        }])

        setQuizState("completed")
      }
      else {
        throw new Error("Failed to submit quiz");
      }
    } catch (err) {
      //console.error("Failed to submit quiz:", err);
      setError("Failed to submit quiz. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getCurrentQuestion = () => {
    return quizData?.questions[currentQuestionIndex];
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < quizData.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const resetQuiz = () => {
    setQuizState("overview");
    setSelectedAnswers({});
    setCurrentQuestionIndex(0);
    setError(null);
  };

  const isQuizComplete = () => {
    return Object.keys(selectedAnswers).length === quizData?.questions.length;
  };

  const getQuestionProgress = () => {
    const answeredQuestions = Object.keys(selectedAnswers).length;
    const totalQuestions = quizData?.questions.length || 0;
    return `${answeredQuestions}/${totalQuestions}`;
  };

  if (loading) {
    return (
      <div className="quiz-viewer">
        <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
          <div className="spinner-border text-warning" role="status">
            <span className="visually-hidden"></span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !quizData) {
    return (
      <div className="quiz-viewer">
        <div className="alert alert-danger">
          <h5>Error Loading Quiz</h5>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!quizData) {
    return (
      <div className="quiz-viewer">
        <div className="alert alert-info">
          <h5>No Quiz Available</h5>
          <p>This lesson does not have an associated quiz.</p>
        </div>
      </div>
    );
  }

  // Quiz completion state
  if (quizState === "completed") {
    const latestAttempt = quizAttempts[quizAttempts.length - 1];
    const passed = latestAttempt?.score >= quizData.passing_percentage;

    return (
      <div className="quiz-viewer">
        <div className="quiz-completion">
          <div className="text-center mb-4">
            <div className={`completion-icon ${passed ? "text-success" : "text-danger"}`}>
              <i className={`fas ${passed ? "fa-check-circle" : "fa-times-circle"} fa-4x`}></i>
            </div>
            <h4 className="mt-3">Quiz {passed ? "Completed!" : "Failed"}</h4>
            <p className="text-muted">You scored {latestAttempt?.score || 0}% on this quiz</p>
          </div>

          <div className="completion-stats">
            <div className="row">
              <div className="col-md-4 text-center">
                <div className="stat-box">
                  <h5>{latestAttempt?.score || 0}%</h5>
                  <small className="text-muted">Your Score</small>
                </div>
              </div>
              <div className="col-md-4 text-center">
                <div className="stat-box">
                  <h5>{quizData.passing_percentage}%</h5>
                  <small className="text-muted">Passing Score</small>
                </div>
              </div>
              <div className="col-md-4 text-center">
                <div className="stat-box">
                  <h5>{quizAttempts.length}</h5>
                  <small className="text-muted">Total Attempts</small>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-4">
            <button className="btn btn-secondary me-2" onClick={resetQuiz}>
              Back to Overview
            </button>
            {!passed && (
              <button className="btn btn-warning" onClick={handleStartQuiz}>
                Retake Quiz
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Taking quiz state
  if (quizState === "taking") {
    const currentQuestion = getCurrentQuestion();

    return (
      <div className="quiz-viewer">
        <div className="quiz-header">
          <div className="quiz-info d-flex justify-content-between align-items-center">
            <div>
              <span className="badge bg-primary me-2">
                Question {currentQuestionIndex + 1} of {quizData.questions.length}
              </span>
              <span className="badge bg-secondary">Progress: {getQuestionProgress()}</span>
            </div>
            <div>
              <span className="text-muted">Quiz: {quizData.title || lesson.title}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        <div className="quiz-content">
          <div className="question-section">
            <div className="question-header mb-4">
              <div className="progress mb-3">
                <div
                  className="progress-bar bg-warning"
                  style={{ width: `${((currentQuestionIndex + 1) / quizData.questions.length) * 100}%` }}
                ></div>
              </div>
              <h5 className="question-title">{currentQuestion.text}</h5>
            </div>

            <div className="options-list mt-4">
              {currentQuestion.options.map((option) => (
                <div key={option.id} className="mb-3">
                  <div className="form-check option-card">
                    <input
                      className="form-check-input"
                      type="radio"
                      name={`question-${currentQuestion.id}`}
                      id={`option-${option.id}`}
                      checked={selectedAnswers[currentQuestion.id] === option.id}
                      onChange={() => handleAnswerChange(currentQuestion.id, option.id)}
                    />
                    <label className="form-check-label w-100" htmlFor={`option-${option.id}`}>
                      {option.text}
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="quiz-navigation mt-4 d-flex justify-content-between">
              <button
                className="btn btn-outline-secondary"
                onClick={prevQuestion}
                disabled={currentQuestionIndex === 0}
              >
                <i className="fas fa-chevron-left me-2"></i>
                Previous
              </button>

              <div>
                {currentQuestionIndex < quizData.questions.length - 1 ? (
                  <button className="btn btn-primary" onClick={nextQuestion}>
                    Next
                    <i className="fas fa-chevron-right ms-2"></i>
                  </button>
                ) : (
                  <button
                    className="btn btn-warning"
                    onClick={handleSubmitQuiz}
                    disabled={!isQuizComplete() || submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check me-2"></i>
                        Submit Quiz
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Question overview */}
            <div className="question-overview mt-4">
              <h6>Question Overview:</h6>
              <div className="question-grid">
                {quizData.questions.map((q, index) => (
                  <button
                    key={q.id}
                    className={`btn btn-sm me-2 mb-2 ${selectedAnswers[q.id]
                        ? "btn-success"
                        : index === currentQuestionIndex
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`}
                    onClick={() => setCurrentQuestionIndex(index)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Overview state (default)
  return (
    <div className="quiz-viewer">
      <div className="quiz-overview">
        <div className="quiz-header-info">
          <span className="quiz-type badge bg-info">Quiz</span>
          <h4 className="quiz-title mt-2">{quizData.title || lesson.title}</h4>
          {quizData.description && (
            <p className="quiz-description text-muted mt-2">{quizData.description}</p>
          )}
        </div>

        <div className="quiz-stats">
          <div className="row">
            <div className="col-md-3">
              <div className="stat-item">
                <span className="stat-label">Questions:</span>
                <span className="stat-value">{quizData.questions.length}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="stat-item">
                <span className="stat-label">Passing Score:</span>
                <span className="stat-value">{quizData.passing_percentage}%</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="stat-item">
                <span className="stat-label">Attempts:</span>
                <span className="stat-value">{quizAttempts.length}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="stat-item">
                <span className="stat-label">Best Score:</span>
                <span className="stat-value">
                  {quizAttempts.length > 0
                    ? `${Math.max(...quizAttempts.map((a) => a.score)).toFixed(2)}%`
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {quizAttempts.length > 0 && (
          <div className="quiz-attempts mt-4">
            <h6>Previous Attempts</h6>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Attempt</th>
                    <th>Date</th>
                    <th>Score</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {quizAttempts.map((attempt, index) => (
                    <tr key={attempt.id}>
                      <td>#{index + 1}</td>
                      <td>{new Date(attempt.completed_at).toLocaleString()}</td>
                      <td>
                        <span className="fw-bold">{attempt.score}%</span>
                      </td>
                      <td>
                        <span
                          className={`badge ${attempt.score >= quizData.passing_percentage ? "bg-success" : "bg-danger"
                            }`}
                        >
                          {attempt.score >= quizData.passing_percentage ? "Pass" : "Fail"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="quiz-actions mt-4">
          <button className="btn btn-warning btn-lg" onClick={handleStartQuiz}>
            <i className="fas fa-play me-2"></i>
            {quizAttempts.length > 0 ? "Retake Quiz" : "Start Quiz"}
          </button>
        </div>

        {quizData.instructions && (
          <div className="quiz-instructions mt-4">
            <h6>Instructions:</h6>
            <div className="alert alert-info">
              <p className="mb-0">{quizData.instructions}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizViewer;